import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import orderService from '../../services/orderService';
import Loading from '../../components/Common/Loading';
import OrderStatusDropdown from '../../components/Orders/OrderStatusDropdown';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';
import DualCurrencyAmount from '../../components/Common/DualCurrencyAmount';
import { AR_LOCALE_LATN, formatSyrianPhone, formatExchangeRate } from '../../utils/formatters';

const STATUS_LIST = ['all', 'pending', 'packed', 'shipped', 'returned', 'canceled'];

const STATUS_COLORS = {
  pending:  { bg: 'rgba(245,158,11,0.12)',  color: '#b45309' },
  packed:   { bg: 'rgba(99,102,241,0.12)',  color: '#4338ca' },
  shipped:  { bg: 'rgba(16,185,129,0.12)', color: '#059669' },
  returned: { bg: 'rgba(239,68,68,0.12)',  color: '#dc2626' },
  canceled: { bg: 'rgba(107,114,128,0.12)',color: '#6b7280' },
};

const OrdersIndex = () => {
  const { get, isRtl } = useLocalization();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [orders, setOrders]     = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [paged, setPaged]       = useState({ total: 0, page: 1, pageSize: 15 });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');

  useEffect(() => {
    loadOrders();
  }, [statusFilter, paged.page]);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      let result;
      if (statusFilter === 'all') {
        result = await orderService.getAll(paged.page, paged.pageSize);
      } else {
        result = await orderService.getByStatus(statusFilter, paged.page, paged.pageSize);
      }
      setOrders(result.items || []);
      setPaged(prev => ({ ...prev, total: result.total || 0 }));
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await orderService.updateStatus(orderId, newStatus);
      loadOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const parseOrderDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value === 'number') {
      const ms = value < 1e12 ? value * 1000 : value;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d;
    }
    const raw = value.toString().trim();
    if (!raw) return null;
    const m = raw.match(/Date\((\d+)\)/i);
    if (m) { const d = new Date(Number(m[1])); return isNaN(d.getTime()) ? null : d; }
    if (/^\d+$/.test(raw)) {
      const num = Number(raw);
      const d = new Date(num < 1e12 ? num * 1000 : num);
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  };

  const filteredOrders = orders.filter(order => {
    const norm = (v) => (v ?? '').toString().toLowerCase();
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      const match =
        norm(order.orderNumber).includes(s) ||
        norm(order.customerName).includes(s) ||
        norm(order.phone).includes(s);
      if (!match) return false;
    }
    if (dateFrom || dateTo) {
      const d = parseOrderDate(order.createdAtUtc);
      if (!d) return false;
      const dayStart = (iso) => { const x = new Date(iso); x.setHours(0,0,0,0); return x; };
      const dayEnd   = (iso) => { const x = new Date(iso); x.setHours(23,59,59,999); return x; };
      if (dateFrom && d < dayStart(dateFrom)) return false;
      if (dateTo   && d > dayEnd(dateTo))     return false;
    }
    return true;
  });

  const getStatusLabel = (status) => {
    const labels = {
      all:      isRtl ? 'الكل'       : 'All',
      pending:  isRtl ? 'معلق'       : 'Pending',
      packed:   isRtl ? 'تم التعبئة' : 'Packed',
      shipped:  isRtl ? 'تم الشحن'  : 'Shipped',
      returned: isRtl ? 'مرتجع'      : 'Returned',
      canceled: isRtl ? 'ملغي'       : 'Canceled',
    };
    return labels[status] || status;
  };

  const columns = [
    {
      header: isRtl ? 'رقم الطلب' : 'Order #',
      accessor: 'orderNumber',
      render: (value, row) => (
        <button
          className="order-number-link"
          onClick={(e) => {
            e.stopPropagation();
            const id = row.id ?? row.orderId ?? row.orderNumber;
            if (id) navigate(`/orders/${id}`);
          }}
        >
          {value}
        </button>
      ),
    },
    {
      header: isRtl ? 'العميل' : 'Customer',
      accessor: 'customerName',
      render: (value, row) => (
        <div className="customer-cell">
          <div className="customer-name">{value}</div>
          {row.phone && <div className="customer-phone" dir="ltr">{formatSyrianPhone(row.phone)}</div>}
        </div>
      ),
    },
    {
      header: isRtl ? 'الإجمالي' : 'Total',
      accessor: 'total',
      render: (value, row) => (
        <span className="order-total">
          <DualCurrencyAmount amountSyp={value} locale={isRtl ? 'ar' : 'en'} rate={row.exchangeRateSypPerUsd} />
        </span>
      ),
    },
    {
      header: isRtl ? 'سعر الصرف' : 'Exchange Rate',
      accessor: 'exchangeRateSypPerUsd',
      render: (value) => (
        <span className="order-exchange-rate">{formatExchangeRate(value, isRtl ? 'ar' : 'en')}</span>
      ),
    },
    {
      header: isRtl ? 'الحالة' : 'Status',
      accessor: 'status',
      render: (value, row) => (
        <OrderStatusDropdown
          currentStatus={value}
          statusKey={row.statusEn || row.statusAr || value}
          statusLabel={isRtl ? (row.statusAr || row.statusEn || value) : (row.statusEn || row.statusAr || value)}
          onStatusChange={handleStatusChange}
          orderId={row.id}
        />
      ),
    },
    {
      header: isRtl ? 'التاريخ' : 'Date',
      accessor: 'createdAtUtc',
      render: (value) => {
        const d = parseOrderDate(value);
        if (!d) return <span className="order-date text-muted">{isRtl ? '—' : '—'}</span>;
        return (
          <span className="order-date">
            {d.toLocaleDateString(isRtl ? AR_LOCALE_LATN : 'en-US', { day: 'numeric', month: 'short', year: 'numeric', numberingSystem: 'latn' })}
          </span>
        );
      },
    },
  ];

  const totalPages = Math.ceil(paged.total / paged.pageSize);

  const pendingCount  = orders.filter(o => (o.statusEn || o.status || '').toLowerCase() === 'pending').length;
  const shippedCount  = orders.filter(o => (o.statusEn || o.status || '').toLowerCase() === 'shipped').length;

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading orders...'} />;
  }

  return (
    <div className="orders-page">
      {/* ── Page Header ── */}
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{get('order_management')}</h1>
            <p className="page-description">{get('order_management_desc')}</p>
          </div>
          <div className="page-header-actions">
            <Button variant="secondary" onClick={loadOrders} disabled={isLoading}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </svg>
              <span className="hidden-mobile">{isRtl ? 'تحديث' : 'Refresh'}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="stats-grid mb-6">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-info">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"></path>
                <rect x="9" y="3" width="6" height="4" rx="1"></rect>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{paged.total}</div>
              <div className="stat-label">{isRtl ? 'إجمالي الطلبات' : 'Total Orders'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon" style={{ background: 'rgba(245,158,11,0.12)', color: '#b45309' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{pendingCount}</div>
              <div className="stat-label">{isRtl ? 'معلق' : 'Pending'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"></path>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{shippedCount}</div>
              <div className="stat-label">{isRtl ? 'تم الشحن' : 'Shipped'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon" style={{ background: 'rgba(99,102,241,0.12)', color: '#4338ca' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="3" width="15" height="13" rx="1"></rect>
                <path d="M16 8h4l3 5v3h-7V8z"></path>
                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                <circle cx="18.5" cy="18.5" r="2.5"></circle>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{filteredOrders.length}</div>
              <div className="stat-label">{isRtl ? 'نتائج الفلتر' : 'Filtered'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Card ── */}
      <div className="card orders-card">
        {/* Filters row */}
        <div className="card-header orders-filters">
          {/* Search */}
          <div className="search-box orders-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={isRtl ? 'بحث بالرقم أو العميل...' : 'Search order or customer...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Date range */}
          <div className="date-range-filter">
            <div className="date-input-wrapper">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                title={isRtl ? 'من تاريخ' : 'From date'}
              />
            </div>
            <span className="date-range-sep">—</span>
            <div className="date-input-wrapper">
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                title={isRtl ? 'إلى تاريخ' : 'To date'}
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                className="date-clear-btn"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                title={isRtl ? 'مسح التاريخ' : 'Clear dates'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Status tabs */}
        <div className="orders-status-bar">
          {STATUS_LIST.map((status) => {
            const active = statusFilter === status;
            const colors = STATUS_COLORS[status];
            return (
              <button
                key={status}
                className={`status-tab ${active ? 'status-tab-active' : ''}`}
                style={active && colors ? { background: colors.bg, color: colors.color, borderColor: colors.color + '44' } : {}}
                onClick={() => {
                  setStatusFilter(status);
                  setPaged(prev => ({ ...prev, page: 1 }));
                }}
              >
                {getStatusLabel(status)}
              </button>
            );
          })}
          <span className="orders-count-badge">
            {filteredOrders.length} {isRtl ? 'طلب' : 'orders'}
          </span>
        </div>

        {/* Scrollable table body */}
        <div className="card-body p-0">
          <div className="orders-table-scroll">
            <Table
              columns={columns}
              data={filteredOrders}
              onRowClick={(row) => {
                const id = row.id ?? row.orderId ?? row.orderNumber;
                if (id) navigate(`/orders/${id}`);
              }}
              emptyMessage={isRtl ? 'لا توجد طلبات' : 'No orders found'}
            />
          </div>
        </div>

        {/* Always-visible pagination */}
        {totalPages > 1 && (
          <div className="card-footer orders-pagination">
            <span className="pagination-info">
              {isRtl
                ? `الصفحة ${paged.page} من ${totalPages} · ${paged.total} طلب`
                : `Page ${paged.page} of ${totalPages} · ${paged.total} orders`}
            </span>
            <div className="pagination-controls">
              <Button
                variant="secondary"
                size="small"
                onClick={() => setPaged(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={paged.page === 1}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points={isRtl ? '9 18 15 12 9 6' : '15 18 9 12 15 6'}></polyline>
                </svg>
                <span className="hidden-mobile">{isRtl ? 'السابق' : 'Prev'}</span>
              </Button>

              {/* Page number pills */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (paged.page <= 3) {
                  page = i + 1;
                } else if (paged.page >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = paged.page - 2 + i;
                }
                return (
                  <button
                    key={page}
                    className={`page-pill ${paged.page === page ? 'page-pill-active' : ''}`}
                    onClick={() => setPaged(prev => ({ ...prev, page }))}
                  >
                    {page}
                  </button>
                );
              })}

              <Button
                variant="secondary"
                size="small"
                onClick={() => setPaged(prev => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
                disabled={paged.page >= totalPages}
              >
                <span className="hidden-mobile">{isRtl ? 'التالي' : 'Next'}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points={isRtl ? '15 18 9 12 15 6' : '9 18 15 12 9 6'}></polyline>
                </svg>
              </Button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        /* ── Orders page layout ── */
        .orders-card { display: flex; flex-direction: column; }

        /* Filters row */
        .orders-filters {
          flex-wrap: wrap !important;
          gap: var(--sp-3) !important;
          align-items: center !important;
          border-bottom: 1px solid var(--border-light) !important;
        }
        .orders-search { flex: 1 1 200px; max-width: 280px !important; }

        /* Date range */
        .date-range-filter {
          display: flex;
          align-items: center;
          gap: var(--sp-2);
          flex-wrap: nowrap;
        }
        .date-input-wrapper {
          display: flex;
          align-items: center;
          gap: 6px;
          background: var(--surface);
          border: 1.5px solid var(--border);
          border-radius: var(--r-lg);
          padding: 6px 10px;
          color: var(--text-secondary);
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .date-input-wrapper:focus-within {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(30,58,95,0.08);
          color: var(--text);
        }
        .date-input-wrapper input[type="date"] {
          border: none;
          outline: none;
          background: transparent;
          font-size: var(--fs-sm);
          color: inherit;
          font-family: inherit;
          width: 120px;
        }
        .date-range-sep {
          color: var(--text-muted);
          font-size: var(--fs-sm);
          user-select: none;
        }
        .date-clear-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px; height: 28px;
          border: none;
          background: rgba(239,68,68,0.1);
          color: #dc2626;
          border-radius: var(--r-full);
          cursor: pointer;
          transition: background 0.2s;
        }
        .date-clear-btn:hover { background: rgba(239,68,68,0.2); }

        /* Status tab bar */
        .orders-status-bar {
          display: flex;
          align-items: center;
          gap: var(--sp-1);
          padding: var(--sp-3) var(--sp-5);
          border-bottom: 1px solid var(--border-light);
          overflow-x: auto;
          scrollbar-width: none;
          flex-shrink: 0;
        }
        .orders-status-bar::-webkit-scrollbar { display: none; }
        .status-tab {
          flex-shrink: 0;
          padding: 5px 14px;
          border-radius: var(--r-pill);
          border: 1.5px solid transparent;
          background: transparent;
          color: var(--text-secondary);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.3px;
          cursor: pointer;
          transition: all 0.18s ease;
          font-family: inherit;
        }
        .status-tab:hover:not(.status-tab-active) {
          background: var(--surface-2);
          color: var(--text);
        }
        .status-tab-active {
          font-weight: 700;
        }
        .orders-count-badge {
          margin-inline-start: auto;
          flex-shrink: 0;
          font-size: 12px;
          color: var(--text-muted);
          white-space: nowrap;
        }

        /* Scrollable table */
        .orders-table-scroll {
          max-height: 55vh;
          overflow-y: auto;
          overflow-x: auto;
        }
        .orders-table-scroll::-webkit-scrollbar {
          width: 5px; height: 5px;
        }
        .orders-table-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .orders-table-scroll::-webkit-scrollbar-thumb {
          background: var(--border);
          border-radius: 10px;
        }
        .orders-table-scroll .table-container { overflow: visible !important; }
        .orders-table-scroll thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: var(--surface) !important;
          box-shadow: 0 1px 0 var(--border);
        }

        /* Pagination footer */
        .orders-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: var(--sp-3);
          padding: var(--sp-3) var(--sp-5);
          border-top: 1px solid var(--border-light);
          background: var(--surface);
          border-radius: 0 0 var(--r-xl) var(--r-xl);
          flex-shrink: 0;
        }
        .pagination-info {
          font-size: var(--fs-sm);
          color: var(--text-secondary);
        }
        .pagination-controls {
          display: flex;
          align-items: center;
          gap: var(--sp-1);
        }
        .page-pill {
          width: 32px; height: 32px;
          display: flex; align-items: center; justify-content: center;
          border-radius: var(--r-lg);
          border: 1.5px solid var(--border);
          background: transparent;
          color: var(--text-secondary);
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.18s ease;
          font-family: inherit;
        }
        .page-pill:hover:not(.page-pill-active) {
          background: var(--surface-2);
          color: var(--text);
          border-color: var(--border-dark, #d1d5db);
        }
        .page-pill-active {
          background: var(--primary);
          color: #fff;
          border-color: var(--primary);
        }

        /* Cell styles */
        .order-number-link {
          background: none;
          border: none;
          color: var(--primary);
          font-weight: var(--fw-semibold);
          cursor: pointer;
          padding: 0;
          font-family: inherit;
          font-size: inherit;
        }
        .order-number-link:hover { text-decoration: underline; }
        .customer-cell { min-width: 120px; }
        .customer-name { font-weight: var(--fw-medium); color: var(--text); }
        .customer-phone { font-size: var(--fs-sm); color: var(--text-muted); margin-top: 2px; }
        .order-total { font-weight: var(--fw-semibold); color: var(--text); }
        .order-exchange-rate { font-size: var(--fs-sm); color: var(--text-muted); white-space: nowrap; }
        .order-date { color: var(--text-secondary); font-size: var(--fs-md); }

        @media (max-width: 768px) {
          .orders-filters { flex-direction: column; align-items: stretch !important; }
          .orders-search { max-width: 100% !important; }
          .date-range-filter { justify-content: flex-start; flex-wrap: wrap; }
          .orders-table-scroll { max-height: 50vh; }
          .date-input-wrapper input[type="date"] { width: 100px; }
          .orders-pagination { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
    </div>
  );
};

export default OrdersIndex;
